import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Clock, ArrowLeft, Check, Globe, Award, Languages, Upload, Image } from 'lucide-react';
import { COUNTRIES, FRANCE_REGIONS, getDepartmentsByRegion, getRegionsByCountry } from '../data/geography';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const SPECIALTIES = [
  { id: 'therapeute', name: 'Thérapeute' },
  { id: 'naturopathe', name: 'Naturopathe' },
  { id: 'sophrologue', name: 'Sophrologue' },
  { id: 'psychologue', name: 'Psychologue' },
  { id: 'osteopathe', name: 'Ostéopathe' },
  { id: 'hypnotherapeute', name: 'Hypnothérapeute' },
  { id: 'coach', name: 'Coach' },
  { id: 'orthophoniste', name: 'Orthophoniste' },
];

const SPOKEN_LANGUAGES = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'Anglais' },
  { code: 'it', name: 'Italien' },
  { code: 'es', name: 'Espagnol' },
  { code: 'de', name: 'Allemand' },
  { code: 'ru', name: 'Russe' },
  { code: 'ro', name: 'Roumain' },
  { code: 'ar', name: 'Arabe' },
  { code: 'pt', name: 'Portugais' },
  { code: 'zh', name: 'Chinois' }
];

const PractitionerSetup = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, isPractitioner } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [slots, setSlots] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    specialty: '',
    bio: '',
    photo_url: '',
    country: 'FR',
    region: '',
    department: '',
    address: '',
    city: '',
    postal_code: '',
    consultation_types: ['cabinet'],
    price: '',
    consultation_duration: 60,
    years_experience: '',
    diplomas: '',
    detailed_description: '',
    languages: [],
    iban: '',
    phone: ''
  });

  // Get departments based on selected region
  const departments = form.region ? getDepartmentsByRegion(form.region) : [];

  const [newSlot, setNewSlot] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '12:00'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (!isPractitioner) {
      navigate('/client/dashboard');
      return;
    }

    const fetchProfile = async () => {
      try {
        const [profileRes, slotsRes] = await Promise.all([
          axios.get(`${API}/practitioners/me/profile`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/slots/me`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        const profile = profileRes.data;
        setForm({
          specialty: profile.specialty || '',
          bio: profile.bio || '',
          photo_url: profile.photo_url || '',
          country: profile.country || 'FR',
          region: profile.region || '',
          department: profile.department || '',
          address: profile.address || '',
          city: profile.city || '',
          postal_code: profile.postal_code || '',
          consultation_types: profile.consultation_types || ['cabinet'],
          price: profile.price?.toString() || '',
          consultation_duration: profile.consultation_duration || 60,
          years_experience: profile.years_experience?.toString() || '',
          diplomas: profile.diplomas?.join(', ') || '',
          detailed_description: profile.detailed_description || '',
          languages: profile.languages || [],
          iban: profile.iban || '',
          phone: profile.phone || ''
        });
        setHasProfile(true);
        setSlots(slotsRes.data);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching profile:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [isAuthenticated, isPractitioner, navigate, token]);

  const handleConsultationTypeChange = (type, checked) => {
    setForm(prev => ({
      ...prev,
      consultation_types: checked 
        ? [...prev.consultation_types, type]
        : prev.consultation_types.filter(t => t !== type)
    }));
  };

  const handleLanguageChange = (langCode, checked) => {
    setForm(prev => ({
      ...prev,
      languages: checked 
        ? [...prev.languages, langCode]
        : prev.languages.filter(l => l !== langCode)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - only JPG and PNG allowed
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez uniquement JPG ou PNG');
      return;
    }

    // Validate file size (500 KB max)
    if (file.size > 500 * 1024) {
      toast.error('Fichier trop volumineux (max 500 Ko). Veuillez compresser votre image.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setForm(prev => ({ ...prev, photo_url: response.data.photo_url }));
      toast.success('Photo uploadée avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.specialty) {
      toast.error('Veuillez sélectionner une spécialité');
      return;
    }
    if (form.consultation_types.length === 0) {
      toast.error('Veuillez sélectionner au moins un type de consultation');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : null,
        consultation_duration: parseInt(form.consultation_duration),
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
        diplomas: form.diplomas ? form.diplomas.split(',').map(d => d.trim()).filter(d => d) : [],
        languages: form.languages
      };

      if (hasProfile) {
        await axios.put(`${API}/practitioners/me`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profil mis à jour');
      } else {
        await axios.post(`${API}/practitioners`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasProfile(true);
        toast.success('Profil créé avec succès');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addSlot = async () => {
    if (!hasProfile) {
      toast.error('Veuillez d\'abord enregistrer votre profil');
      return;
    }

    try {
      const response = await axios.post(`${API}/slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(prev => [...prev, response.data]);
      toast.success('Créneau ajouté');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  const deleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlots(prev => prev.filter(s => s.id !== slotId));
      toast.success('Créneau supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Group slots by day
  const slotsByDay = slots.reduce((acc, slot) => {
    if (!acc[slot.day_of_week]) acc[slot.day_of_week] = [];
    acc[slot.day_of_week].push(slot);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="practitioner-setup">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/practitioner/dashboard')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </button>
          <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
            {hasProfile ? 'Modifier mon profil' : 'Configurer mon profil'}
          </h1>
          <p className="text-muted-foreground">
            {hasProfile 
              ? 'Mettez à jour vos informations et disponibilités'
              : 'Complétez votre profil pour commencer à recevoir des rendez-vous'}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Profile Form */}
          <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="profile-form">
            <h2 className="font-heading text-xl font-medium mb-6">Informations du profil</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Specialty */}
              <div className="space-y-2">
                <Label htmlFor="specialty">Spécialité *</Label>
                <Select value={form.specialty} onValueChange={(value) => setForm({ ...form, specialty: value })}>
                  <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-specialty">
                    <SelectValue placeholder="Choisir une spécialité" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map((spec) => (
                      <SelectItem key={spec.id} value={spec.id}>{spec.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio">Présentation courte</Label>
                <Textarea
                  id="bio"
                  placeholder="Décrivez brièvement votre approche en 2-3 phrases..."
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="form-textarea min-h-[80px]"
                  data-testid="input-bio"
                />
              </div>

              {/* Detailed Description */}
              <div className="space-y-2">
                <Label htmlFor="detailed_description">Description détaillée</Label>
                <Textarea
                  id="detailed_description"
                  placeholder="Décrivez en détail votre parcours, votre philosophie, vos méthodes de travail..."
                  value={form.detailed_description}
                  onChange={(e) => setForm({ ...form, detailed_description: e.target.value })}
                  className="form-textarea min-h-[150px]"
                  data-testid="input-detailed-description"
                />
              </div>

              {/* Years Experience */}
              <div className="space-y-2">
                <Label htmlFor="years_experience" className="flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Années d'expérience
                </Label>
                <Input
                  id="years_experience"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="Ex: 5"
                  value={form.years_experience}
                  onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                  className="rounded-xl border-sand-200"
                  data-testid="input-experience"
                />
              </div>

              {/* Diplomas/Certifications */}
              <div className="space-y-2">
                <Label htmlFor="diplomas">Diplômes et Certifications</Label>
                <Textarea
                  id="diplomas"
                  placeholder="Ex: Diplôme de Naturopathie CENATHO, Formation en Aromathérapie... (séparés par des virgules)"
                  value={form.diplomas}
                  onChange={(e) => setForm({ ...form, diplomas: e.target.value })}
                  className="form-textarea min-h-[80px]"
                  data-testid="input-diplomas"
                />
                <p className="text-xs text-muted-foreground">Séparez chaque diplôme par une virgule</p>
              </div>

              {/* Spoken Languages */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  Langues parlées
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {SPOKEN_LANGUAGES.map((lang) => (
                    <div key={lang.code} className="flex items-center gap-2">
                      <Checkbox
                        id={`lang-${lang.code}`}
                        checked={form.languages.includes(lang.code)}
                        onCheckedChange={(checked) => handleLanguageChange(lang.code, checked)}
                        data-testid={`checkbox-lang-${lang.code}`}
                      />
                      <Label htmlFor={`lang-${lang.code}`} className="font-normal cursor-pointer text-sm">
                        {lang.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Photo Upload */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Photo de profil
                </Label>
                
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-sand-300 overflow-hidden bg-sand-50 flex items-center justify-center">
                    {form.photo_url ? (
                      <img 
                        src={form.photo_url.startsWith('/api/photos') || form.photo_url.startsWith('/uploads')
                          ? `${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`
                          : form.photo_url
                        }
                        alt="Aperçu"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-sand-400" />
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex-1 space-y-2">
                    <label 
                      htmlFor="photo-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 bg-sage-50 hover:bg-sage-100 text-sage-700 rounded-xl cursor-pointer transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Upload en cours...' : 'Choisir une photo'}
                    </label>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handlePhotoUpload}
                      disabled={uploading}
                      className="hidden"
                      data-testid="input-photo-upload"
                    />
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG ou WebP. Max 5 MB.
                    </p>
                    
                    {/* Optional URL input */}
                    <div className="pt-2">
                      <Input
                        id="photo_url"
                        placeholder="Ou entrez une URL externe..."
                        value={form.photo_url}
                        onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                        className="rounded-xl border-sand-200 text-sm"
                        data-testid="input-photo-url"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Pays *
                </Label>
                <Select 
                  value={form.country} 
                  onValueChange={(value) => {
                    const newForm = { ...form, country: value };
                    if (value !== 'FR') {
                      newForm.region = '';
                      newForm.department = '';
                    }
                    setForm(newForm);
                  }}
                >
                  <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-country">
                    <SelectValue placeholder="Choisir un pays" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Region (for all countries with regions) */}
              {form.country && getRegionsByCountry(form.country).length > 0 && (
                <div className="space-y-2 animate-fadeIn">
                  <Label>Région</Label>
                  <Select 
                    value={form.region} 
                    onValueChange={(value) => setForm({ ...form, region: value, department: '' })}
                  >
                    <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-region">
                      <SelectValue placeholder="Choisir une région" />
                    </SelectTrigger>
                    <SelectContent>
                      {getRegionsByCountry(form.country).map((region) => (
                        <SelectItem key={region.code} value={region.code}>{region.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Department (France only, when region selected) */}
              {form.country === 'FR' && form.region && (
                <div className="space-y-2 animate-fadeIn">
                  <Label>Département</Label>
                  <Select 
                    value={form.department} 
                    onValueChange={(value) => setForm({ ...form, department: value })}
                  >
                    <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-department">
                      <SelectValue placeholder="Choisir un département" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.code} value={dept.code}>
                          {dept.code} - {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Adresse du cabinet</Label>
                <Input
                  id="address"
                  placeholder="123 rue de la Santé"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="rounded-xl border-sand-200"
                  data-testid="input-address"
                />
              </div>

              {/* City & Postal Code */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Ville</Label>
                  <Input
                    id="city"
                    placeholder="Paris"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input
                    id="postal_code"
                    placeholder="75001"
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="input-postal"
                  />
                </div>
              </div>

              {/* Phone & IBAN */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone professionnel</Label>
                  <Input
                    id="phone"
                    placeholder="+33 6 12 34 56 78"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN (pour recevoir les paiements)</Label>
                  <Input
                    id="iban"
                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                    value={form.iban}
                    onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                    className="rounded-xl border-sand-200 font-mono text-sm"
                    data-testid="input-iban"
                  />
                  <p className="text-xs text-muted-foreground">
                    Votre IBAN est nécessaire pour recevoir vos paiements et pour le prélèvement de l'abonnement mensuel.
                  </p>
                </div>
              </div>

              {/* Consultation Types */}
              <div className="space-y-3">
                <Label>Types de consultation *</Label>
                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="type-cabinet"
                      checked={form.consultation_types.includes('cabinet')}
                      onCheckedChange={(checked) => handleConsultationTypeChange('cabinet', checked)}
                      data-testid="checkbox-cabinet"
                    />
                    <Label htmlFor="type-cabinet" className="font-normal cursor-pointer">En cabinet</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="type-visio"
                      checked={form.consultation_types.includes('visio')}
                      onCheckedChange={(checked) => handleConsultationTypeChange('visio', checked)}
                      data-testid="checkbox-visio"
                    />
                    <Label htmlFor="type-visio" className="font-normal cursor-pointer">En visio</Label>
                  </div>
                </div>
              </div>

              {/* Price & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Tarif (€)</Label>
                  <Input
                    id="price"
                    type="number"
                    placeholder="60"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="rounded-xl border-sand-200"
                    data-testid="input-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée (min)</Label>
                  <Select 
                    value={form.consultation_duration.toString()} 
                    onValueChange={(value) => setForm({ ...form, consultation_duration: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-xl border-sand-200" data-testid="select-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                      <SelectItem value="120">120 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-sage-500 hover:bg-sage-600 rounded-xl"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  'Enregistrement...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {hasProfile ? 'Mettre à jour' : 'Créer mon profil'}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Time Slots */}
          <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="time-slots-section">
            <h2 className="font-heading text-xl font-medium mb-6">Disponibilités</h2>
            
            {!hasProfile ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Enregistrez d'abord votre profil pour configurer vos disponibilités</p>
              </div>
            ) : (
              <>
                {/* Add Slot Form */}
                <div className="bg-sand-50 rounded-xl p-4 mb-6">
                  <h3 className="font-medium mb-3">Ajouter un créneau</h3>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label className="text-xs">Jour</Label>
                      <Select 
                        value={newSlot.day_of_week.toString()} 
                        onValueChange={(value) => setNewSlot({ ...newSlot, day_of_week: parseInt(value) })}
                      >
                        <SelectTrigger className="rounded-lg border-sand-200 bg-white" data-testid="new-slot-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_NAMES.map((day, index) => (
                            <SelectItem key={index} value={index.toString()}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Début</Label>
                      <Input
                        type="time"
                        value={newSlot.start_time}
                        onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                        className="rounded-lg border-sand-200 bg-white"
                        data-testid="new-slot-start"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fin</Label>
                      <Input
                        type="time"
                        value={newSlot.end_time}
                        onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                        className="rounded-lg border-sand-200 bg-white"
                        data-testid="new-slot-end"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={addSlot}
                    variant="outline"
                    size="sm"
                    className="w-full"
                    data-testid="add-slot-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                </div>

                {/* Current Slots */}
                {Object.keys(slotsByDay).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Aucun créneau configuré</p>
                    <p className="text-sm">Ajoutez vos disponibilités ci-dessus</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(slotsByDay)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([day, daySlots]) => (
                        <div key={day} className="border-b border-sand-100 pb-4 last:border-0">
                          <h4 className="font-medium text-foreground mb-2">{DAY_NAMES[Number(day)]}</h4>
                          <div className="flex flex-wrap gap-2">
                            {daySlots
                              .sort((a, b) => a.start_time.localeCompare(b.start_time))
                              .map((slot) => (
                                <div 
                                  key={slot.id}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-sage-50 text-sage-700 rounded-lg text-sm"
                                  data-testid={`slot-${slot.id}`}
                                >
                                  <span>{slot.start_time} - {slot.end_time}</span>
                                  <button
                                    onClick={() => deleteSlot(slot.id)}
                                    className="text-sage-400 hover:text-red-500 transition-colors"
                                    data-testid={`delete-slot-${slot.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerSetup;
